import { Download, FileText, MoreHorizontal, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import NotesCheckbox from '@/components/notes/notes-checkbox';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getUserNoteFileBlob } from '@/lib/api/user-notes';
import { sortTodoItemsForRender } from '@/lib/notes/notes-helpers';
import { formatAttachmentSize } from '@/lib/messages/messages-helpers';
import { withAppTimeZone } from '@/lib/timezone';
import { cn } from '@/lib/utils';

function blockTimeLabel(createdAt) {
  const t = new Date(createdAt || '').getTime();
  if (Number.isNaN(t)) return '';
  return new Date(t).toLocaleTimeString(undefined, withAppTimeZone({ hour: '2-digit', minute: '2-digit', hour12: false }));
}

function blockCopyText(block) {
  if (block.type === 'note') return block.text;
  if (block.type === 'todos') {
    return block.items.map((item) => `${item.done ? '[x]' : '[ ]'} ${item.label}`).join('\n');
  }
  return block.file?.filename || '';
}

async function downloadNoteFile(file) {
  const blob = await getUserNoteFileBlob(file.fileId);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = file.filename || 'download';
  anchor.rel = 'noopener';
  anchor.click();
  URL.revokeObjectURL(url);
}

/** Hover-revealed ⋯ menu: copy text / download (files) / delete. Kept deliberately small. */
function BlockActionsMenu({ block, onDelete }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(blockCopyText(block));
      toast.success('Copied');
    } catch {
      toast.error("Couldn't copy");
    }
    setOpen(false);
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      await downloadNoteFile(block.file);
    } catch {
      toast.error("Couldn't download file");
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Note actions"
          className={cn(
            'absolute right-1 top-1 z-10 h-6 w-6 rounded-md bg-card text-muted-foreground transition-opacity hover:bg-muted',
            open ? 'opacity-100' : 'opacity-0 focus-visible:opacity-100 group-hover:opacity-100',
          )}
        >
          <MoreHorizontal className="size-3.5" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-1" align="end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-full justify-start px-3 text-xs font-medium"
          onClick={() => void handleCopy()}
        >
          Copy text
        </Button>
        {block.type === 'file' ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-full justify-start px-3 text-xs font-medium"
            disabled={busy}
            onClick={() => void handleDownload()}
          >
            <Download className="size-3.5" aria-hidden />
            Download
          </Button>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-full justify-start px-3 text-xs font-medium text-rose-600 hover:text-rose-600"
          onClick={() => {
            setOpen(false);
            onDelete?.(block.id);
          }}
        >
          <Trash2 className="size-3.5" aria-hidden />
          Delete
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function NoteBody({ block }) {
  return <p className="m-0 whitespace-pre-wrap pr-[34px] text-[13px] leading-normal">{block.text}</p>;
}

function TodosBody({ block, onToggleItem }) {
  return (
    <div className="flex flex-col">
      {sortTodoItemsForRender(block.items).map((item, index) => (
        <div
          key={item.id}
          className={cn('flex items-start gap-[9px] py-[5px]', index > 0 && 'border-t border-border/60')}
        >
          <NotesCheckbox
            checked={item.done}
            label={item.label}
            onToggle={(next) => onToggleItem?.(block.id, item.id, next)}
          />
          <span
            className={cn(
              'min-w-0 flex-1 text-[13px] leading-snug',
              item.done && 'text-muted-foreground line-through',
            )}
          >
            {item.label}
          </span>
        </div>
      ))}
    </div>
  );
}

function FileBody({ block }) {
  const file = block.file;
  const meta = [formatAttachmentSize(file.size)].filter(Boolean).join(' · ');

  // Native drag-out (Electron): hand the bytes to main, which stages a temp file and
  // starts an OS drag — dropping onto a chat composer then reads as a normal file drop.
  // In the browser (web mode) there is no startDrag; the drag simply does nothing.
  const handleDragStart = (event) => {
    if (!window.electronAPI?.notesStartFileDrag) return;
    event.preventDefault();
    void (async () => {
      try {
        const blob = await getUserNoteFileBlob(file.fileId);
        const bytes = new Uint8Array(await blob.arrayBuffer());
        window.electronAPI.notesStartFileDrag({ filename: file.filename, bytes });
      } catch {
        toast.error("Couldn't start the drag — file not available");
      }
    })();
  };

  return (
    <div
      className="flex cursor-grab items-center gap-2.5 active:cursor-grabbing"
      draggable
      onDragStart={handleDragStart}
      title="Drag into a chat composer to attach"
    >
      <span className="flex size-[34px] shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <FileText className="size-4" aria-hidden />
      </span>
      <span className="min-w-0">
        <b className="block truncate pr-[30px] text-[12.5px] font-bold">{file.filename}</b>
        {meta ? <em className="text-[11px] not-italic text-muted-foreground">{meta}</em> : null}
      </span>
    </div>
  );
}

/**
 * One stream card (note / todos / file): bg-card rounded card with the created time
 * top-right and the hover ⋯ actions.
 */
function NotesBlock({ block, onToggleItem, onDelete }) {
  return (
    <div className="group relative rounded-[10px] border border-border bg-card px-[11px] py-[9px]">
      <span className="pointer-events-none absolute right-[11px] top-[9px] text-[10px] text-muted-foreground/80 group-hover:opacity-0">
        {blockTimeLabel(block.createdAt)}
      </span>
      <BlockActionsMenu block={block} onDelete={onDelete} />
      {block.type === 'note' ? <NoteBody block={block} /> : null}
      {block.type === 'todos' ? <TodosBody block={block} onToggleItem={onToggleItem} /> : null}
      {block.type === 'file' ? <FileBody block={block} /> : null}
    </div>
  );
}

export default NotesBlock;
